import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { mySurveyProgress } from '@/lib/pulse/surveyApi';
import { ClipboardList } from 'lucide-react';

interface Props {
  orgId: string;
}

export function ActiveSurveyCard({ orgId }: Props) {
  const { data } = useQuery({
    queryKey: ['my_survey_progress', orgId],
    queryFn: async () => {
      const { data, error } = await mySurveyProgress(orgId);
      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
  });

  if (!data || data.active !== true) return null;

  const { survey_topic, answered, total_anchors } = data;
  const pct = total_anchors > 0 ? Math.round((answered / total_anchors) * 100) : 0;
  const done = answered >= total_anchors && total_anchors > 0;

  return (
    <Card className="border-primary/40">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <ClipboardList className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div>
              <CardTitle className="font-heading text-base">{survey_topic}</CardTitle>
              <CardDescription>
                {done
                  ? 'All set. Tap to review or change your answers.'
                  : answered === 0
                    ? 'Your team is running a quick check-in.'
                    : `You're ${answered} of ${total_anchors} through.`}
              </CardDescription>
            </div>
          </div>
          {done && <Badge variant="secondary">Done</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <Progress value={pct} />
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm">
            <Link to="/app/employee/survey">
              {done ? 'Review answers' : answered === 0 ? 'Start' : 'Continue'}
            </Link>
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to="/app/employee/charles">Talk it through with Charles</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
